/**
 * Put a person's card in the middle of the tree, once that is actually possible.
 *
 * WHY THIS EXISTS
 * ---------------
 * The tree used to centre on you with a fixed 50ms delay after building the
 * chart. With 18,168 cards that is a race the browser can lose: the container
 * has no size yet, so "the middle" is measured from nothing and the view lands
 * in empty space with a few cards clipped at the top. Sam hit exactly that on
 * the live site (2026-09-17).
 *
 * THE ONE CONDITION THAT MATTERS
 * ------------------------------
 * The SVG must have real dimensions. An earlier version of this file also
 * waited for the node to have finite x/y — an audit proved that guard is DEAD:
 * family-chart lays out synchronously (`store.updateTree` → `calcTree()`, no
 * timers anywhere in the layout path) and explicitly backfills `d.x = 0` for
 * anything missing. So coordinates are always finite by the time we run, and
 * `Number.isFinite(0)` is true anyway, so it would wave the degenerate value
 * through. It is gone rather than left in looking like protection.
 *
 * WHEN IT CANNOT SUCCEED
 * ----------------------
 * It gives up rather than spinning, and it SAYS SO. The version this replaces
 * failed silently, having also deleted the console.warn that used to be here —
 * so a regression on production would have left nothing to look at.
 */

// Retries, not milliseconds. On a slow machine frames are long, so this is
// comfortably more than the ~2s it would be at 60fps; in a background tab
// requestAnimationFrame does not fire at all, so the budget is not consumed.
const MAX_FRAMES = 120;

/**
 * @param {object}   o
 * @param {function} o.getChart  returns the family-chart instance, or null
 * @param {string}   o.personId  whose card to centre
 * @param {function} [o.onDone]  called with true if centred, false if not
 * @param {object}   o.f3        the family-chart module
 * @param {function} [o.raf]     requestAnimationFrame (injected for testing)
 * @returns {function} cancel — call it before starting another, and on unmount
 */
export function centreOnPerson({ getChart, personId, onDone, f3, raf }) {
  const schedule = raf || ((cb) => requestAnimationFrame(cb));
  let cancelled = false;
  let frames = 0;

  const finish = (ok, why) => {
    if (!ok) {
      // The diagnostic the old code had and the first version of this file
      // dropped. If the tree ever opens wrong again there is something to read.
      // eslint-disable-next-line no-console
      console.warn(`[familyTree] could not centre on ${personId}: ${why}`);
    }
    if (onDone) onDone(ok);
  };

  const attempt = () => {
    if (cancelled) return;

    const chart = getChart();
    if (!chart || !chart.svg) return retry("no chart yet");
    if (!personId) return finish(false, "no person to centre on");

    // THE condition: has the container been given a size?
    const dim = chart.svg.getBoundingClientRect();
    if (!dim || dim.width < 1 || dim.height < 1) return retry("container has no size");

    let node;
    try {
      node = chart.store.getTree().data.find((n) => n.data.id === String(personId));
    } catch (err) {
      return retry("tree not readable yet");
    }
    if (!node) return retry("person not in the rendered tree");

    try {
      f3.handlers.cardToMiddle({
        datum: node,
        svg: chart.svg,
        svg_dim: dim,
        transition_time: 1000,
      });
      finish(true);
    } catch (err) {
      finish(false, `cardToMiddle threw: ${err && err.message}`);
    }
  };

  const retry = (why) => {
    frames += 1;
    if (frames > MAX_FRAMES) return finish(false, `gave up after ${MAX_FRAMES} frames — ${why}`);
    schedule(attempt);
  };

  schedule(attempt);
  return () => { cancelled = true; };
}

export default centreOnPerson;
